import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ZONE_ORDER, type ZoneId } from "./spatial/constants";

const LABELS: Record<ZoneId, string> = {
  hero: "Vault",
  presale: "Presale",
  tokenomics: "Tokenomics",
  staking: "Staking",
  roadmap: "Roadmap",
  footer: "Trust",
};

/**
 * Right-edge fixed wayfinder that shows where the user is in the corridor.
 * Click any dot to smooth-scroll to that zone (which the camera follows).
 *
 * "Alive" signals (FANZ Standards):
 * - Active dot pulses with a soft inner glow.
 * - Connector line fills based on scroll progress.
 */
export function ZoneIndicator() {
  const [zoneIdx, setZoneIdx] = useState(0);
  const [progress, setProgress] = useState(0);

  const [pastSpatial, setPastSpatial] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const spatialEnd = window.innerHeight * 6;
      setPastSpatial(window.scrollY > spatialEnd);
      const t = spatialEnd > 0 ? Math.min(1, window.scrollY / spatialEnd) : 0;
      setProgress(t);
      const idx = Math.min(
        ZONE_ORDER.length - 1,
        Math.floor(t * ZONE_ORDER.length),
      );
      setZoneIdx(idx);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (i: number) => {
    const spatialEnd = window.innerHeight * 6;
    const total = ZONE_ORDER.length - 1;
    const t = total > 0 ? i / total : 0;
    window.scrollTo({ top: t * spatialEnd, behavior: "smooth" });
  };

  if (pastSpatial) return null;

  return (
    <div className="pointer-events-auto fixed right-4 top-1/2 z-30 hidden -translate-y-1/2 md:block">
      <div className="relative flex flex-col items-end gap-3 rounded-2xl border border-white/[0.08] bg-black/40 px-3 py-4 backdrop-blur-xl">
        {/* Vertical track */}
        <div className="absolute right-[34px] top-5 h-[calc(100%-40px)] w-[2px] overflow-hidden rounded-full bg-white/[0.06]">
          <div
            className="h-full w-full origin-top bg-gradient-to-b from-accent via-magenta to-cyan"
            style={{
              transform: `scaleY(${progress})`,
              transition: "transform 200ms linear",
            }}
          />
        </div>

        {ZONE_ORDER.map((id, i) => {
          const active = i === zoneIdx;
          return (
            <button
              key={id}
              onClick={() => goTo(i)}
              className="group relative flex items-center gap-3"
              aria-label={`Jump to ${LABELS[id]}`}
            >
              <span
                className={`text-[10px] font-medium uppercase tracking-[0.18em] transition-all ${
                  active ? "text-white opacity-100" : "text-gray-300 opacity-0 group-hover:opacity-100"
                }`}
              >
                {LABELS[id]}
              </span>
              <span className="relative grid h-3 w-3 place-items-center">
                <span
                  className={`block h-3 w-3 rounded-full border transition-all ${
                    active
                      ? "border-accent bg-accent shadow-[0_0_14px_rgba(192,132,252,0.7)]"
                      : "border-white/30 bg-transparent"
                  }`}
                />
                {active && (
                  <motion.span
                    initial={{ scale: 1, opacity: 0.6 }}
                    animate={{ scale: 1.8, opacity: 0 }}
                    transition={{ duration: 1.4, repeat: Infinity }}
                    className="absolute h-3 w-3 rounded-full bg-accent"
                  />
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
